<template>
<v-card class="mx-auto" width="90%" style="margin-bottom: 15px;">
  <v-container fluid class="my-5">
    <v-layout row justify-center>
      <v-tabs v-model="currentTab">
          <v-tab>{{ $t('views.manageUserModels.tabUsers') }}</v-tab>
          <v-tab v-if="authUser.isManager">{{ $t('views.manageUserModels.tabReferences') }}</v-tab>
          <v-tab v-if="authUser.isManager">{{ $t('views.manageUserModels.tabEkrm') }}</v-tab>
          <v-tab v-if="authUser.isManager">{{ $t('views.manageUserModels.tabRoles') }}</v-tab>
          <v-tab>{{ $t('views.manageUserModels.tabGroups') }}</v-tab>
          <v-tab v-if="authUser.isAdmin">{{ $t('views.manageUserModels.tabAuditTrail') }}</v-tab>
      </v-tabs>
      <template v-if="currentTab === 0">
        <v-row>
          <v-col cols="12">
            <v-data-table :headers="computedHeadersUsers" :items="managedUsers" class="elevation-1" style="width: 95%;" :loading="loading" :search="searchManagedUsers">
              <template v-slot:top>
                <v-toolbar flat color="white">
                  <v-btn
                    v-if="hasChanges"
                    color="success"
                    :loading="savingChanges"
                    @click="saveAllChanges"
                    class="mr-2"
                  >
                    <v-icon left>mdi-content-save</v-icon>
                    {{ $t('views.manageUserModels.saveChanges') }}
                  </v-btn>
                  <v-btn
                    v-if="hasChanges"
                    color="grey"
                    outlined
                    :disabled="savingChanges"
                    @click="cancelChanges"
                    class="mr-2"
                  >
                    <v-icon left>mdi-cancel</v-icon>
                    {{ $t('views.manageUserModels.cancel') }}
                  </v-btn>
                  <v-chip
                    v-if="hasChanges"
                    color="warning"
                    small
                    class="mr-2"
                  >
                    {{ $t('views.manageUserModels.unsavedChanges', { count: Object.keys(pendingChanges).length }) }}
                  </v-chip>
                  <v-spacer></v-spacer>
                  <v-text-field
                    v-model="searchManagedUsers"
                    append-icon="mdi-magnify"
                    :label="$t('views.manageUserModels.search')"
                    single-line
                    hide-details
                  ></v-text-field>
                </v-toolbar>
              </template>
              <template v-slot:item.clients="{ item }">
                <div :class="{ 'pending-changes': pendingChanges[item.id] }">
                  <multi-select :value="selectedModels[item.id]" :items="allModels" @input="updateUserClients(item.id, $event)" :label="$t('views.manageUserModels.selectModels')"/>
                  <v-chip v-if="pendingChanges[item.id]" x-small color="orange" text-color="white" class="ml-2">
                    <v-icon x-small left>mdi-clock-outline</v-icon>
                    {{ $t('views.manageUserModels.pending') }}
                  </v-chip>
                </div>
              </template>
              <template v-slot:item.actions="{ item }" v-if="authUser.isAdmin">
                <span v-if="!deleteQueue.includes(item.id)">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn icon v-on="on" @click.stop="deleteDialog = true; deleteItem = item">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                      <v-dialog v-model="deleteDialog" max-width="400">
                        <v-card>
                          <v-card-title class="headline">{{ $t('views.manageUserModels.deleteDialogTitle') }}</v-card-title>
                          <v-card-text>
                            {{ $t('views.manageUserModels.deleteDialogText', { userName: deleteItem.firstName + ' ' + deleteItem.lastName }) }}
                            <br/>
                            {{ $t('views.manageUserModels.deleteDialogConfirmation') }}
                          </v-card-text>
                          <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn color="gray darken-1" text @click="deleteDialog = false">{{ $t('views.manageUserModels.cancel') }}</v-btn>
                            <v-btn color="red darken-1" text @click="triggerDeleteUser(deleteItem.id); deleteDialog = false">{{ $t('views.manageUserModels.deleteButton') }}</v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-dialog>
                    </template>
                    {{ $t('views.manageUserModels.deleteUserTooltip') }}
                  </v-tooltip>
                </span>
                <v-chip class="ma-2" color="red" text-color="white" v-else>{{ $t('views.manageUserModels.deleting') }}</v-chip>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </template>
      <template v-if="authUser.isManager && currentTab === 1">
        <v-row>
          <v-col cols="12">
            <reference-manager reference-type="references" key="references" />
          </v-col>
        </v-row>
      </template>
      <template v-if="authUser.isManager && currentTab === 2">
        <v-row>
          <v-col cols="12">
            <reference-manager reference-type="ekrms" key="ekrms" />
          </v-col>
        </v-row>
      </template>
      <template v-if="authUser.isManager && currentTab === 3">
        <v-row>
          <v-col cols="12">
            <role-manager />
          </v-col>
        </v-row>
      </template>
      <template v-if="(authUser.isManager && currentTab === 4) || (authUser.isSupervisor && currentTab === 1)">
        <v-row>
          <v-col cols="12">
            <group-manager />
          </v-col>
        </v-row>
      </template>
      <template v-if="authUser.isAdmin && ((authUser.isManager && currentTab === 5) || (authUser.isSupervisor && currentTab === 2))">
        <v-row>
          <v-col cols="12">
            <audit-trail-viewer @show-alert="showAlert" />
          </v-col>
        </v-row>
      </template>
    </v-layout>
  </v-container>
  <!-- Alert Snackbar -->
  <v-snackbar
    v-model="alert.show"
    :color="alert.type"
    :timeout="5000"
    bottom
    right
  >
    {{ alert.message }}
    <template v-slot:action="{ attrs }">
      <v-btn
        color="white"
        text
        v-bind="attrs"
        @click="alert.show = false"
      >
        {{ $t('views.manageUserModels.alertClose') }}
      </v-btn>
    </template>
  </v-snackbar>
</v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import MultiSelect from '@/components/MultiSelect.vue'
import ReferenceManager from '@/components/ReferenceManager.vue'
import RoleManager from '@/components/RoleManager.vue'
import GroupManager from '@/components/GroupManager.vue'
import AuditTrailViewer from '@/components/AuditTrailViewer.vue'

export default {
  data () {
    return {
      selectedModels: {},
      originalModels: {}, // Track original state from database
      pendingChanges: {}, // Track which users have pending changes
      savingChanges: false, // Loading state for batch save
      loading: true,
      currentTab: 0,
      searchManagedUsers: '',
      deleteDialog: false,
      deleteItem: '',
      deleteQueue: [],
      alert: {
        show: false,
        type: 'success',
        message: ''
      }
    }
  },
  methods: {
    ...mapActions('manager', [
      'loadManagedUsers',
      'loadAllModels',
      'batchSaveModelUserAccess',
      'deleteUser'
    ]),
    updateUserClients: function (user, clients) {
      // Update local state
      this.$set(this.selectedModels, user, clients)

      // Check if this represents a change from original state
      const originalClients = this.originalModels[user] || []
      const hasChanged = JSON.stringify([...clients].sort()) !== JSON.stringify([...originalClients].sort())

      // Track pending changes
      if (hasChanged) {
        this.$set(this.pendingChanges, user, true)
      } else {
        this.$delete(this.pendingChanges, user)
      }
    },
    saveAllChanges: async function () {
      this.savingChanges = true
      try {
        const pendingUserIds = Object.keys(this.pendingChanges)

        // Build the userChanges array for batch processing
        const userChanges = pendingUserIds.map(userId => {
          const currentClients = this.selectedModels[userId] || []
          const originalClients = this.originalModels[userId] || []

          // Determine enabled and disabled clients
          const enabledClients = currentClients.filter(x => !originalClients.includes(x))
          const disabledClients = originalClients.filter(x => !currentClients.includes(x))

          return {
            userId: userId,
            enabledClients: enabledClients,
            disabledClients: disabledClients
          }
        })

        // Make single batch call to cloud function
        const result = await this.batchSaveModelUserAccess({
          userChanges: userChanges
        })

        if (result && result.success) {
          // Update original state and clear pending changes for all users
          pendingUserIds.forEach(userId => {
            const currentClients = this.selectedModels[userId] || []
            this.$set(this.originalModels, userId, [...currentClients])
            this.$delete(this.pendingChanges, userId)
          })

          this.showAlert({
            type: 'success',
            message: result.message || this.$t('views.manageUserModels.saveSuccessMessage', { count: pendingUserIds.length })
          })
        } else {
          this.showAlert({
            type: 'error',
            message: this.$t('views.manageUserModels.saveErrorMessage')
          })
        }
      } catch (error) {
        console.error('Error during batch save:', error)
        this.showAlert({
          type: 'error',
          message: this.$t('views.manageUserModels.saveErrorWithDetails', { error: error.message || 'Unknown error' })
        })
      } finally {
        this.savingChanges = false
      }
    },
    cancelChanges: function () {
      // Reset selectedModels to match originalModels
      Object.keys(this.pendingChanges).forEach(userId => {
        const originalClients = this.originalModels[userId] || []
        this.$set(this.selectedModels, userId, [...originalClients])
      })

      // Clear pending changes
      this.pendingChanges = {}

      this.showAlert({
        type: 'info',
        message: this.$t('views.manageUserModels.cancelledMessage')
      })
    },
    triggerDeleteUser (id) {
      this.deleteQueue.push(id)
      this.deleteUser({
        uid: id
      }).then((result) => {
        if (result && result.success) {
          this.showAlert({
            type: 'success',
            message: this.$t('views.manageUserModels.deleteSuccessMessage', { userName: result.deletedUser.name })
          })
        }
      }).catch((error) => {
        this.showAlert({
          type: 'error',
          message: this.$t('views.manageUserModels.deleteErrorMessage', { error: error.message || 'Unknown error' })
        })
      }).finally(() => {
        const index = this.deleteQueue.indexOf(id)
        if (index > -1) {
          this.deleteQueue.splice(index, 1)
        }
      })
    },
    showAlert (alertData) {
      this.alert = {
        show: true,
        type: alertData.type || 'info',
        message: alertData.message || 'Action completed'
      }
      setTimeout(() => {
        this.alert.show = false
      }, 5000)
    }
  },
  computed: {
    ...mapGetters('auth', [
      'authUser'
    ]),
    ...mapGetters('manager', [
      'managedUsers',
      'allModels'
    ]),
    computedHeadersUsers () {
      const baseHeaders = [
        { text: this.$t('views.manageUserModels.headerEmail'), value: 'email', width: '40%' },
        { text: this.$t('views.manageUserModels.headerModels'), value: 'clients', width: '50%', sortable: false, filterable: false }
      ]

      if (this.authUser && this.authUser.isAdmin) {
        baseHeaders.push({ text: this.$t('views.manageUserModels.headerActions'), value: 'actions', width: '10%', sortable: false, filterable: false })
      }

      return baseHeaders
    },
    hasChanges () {
      return Object.keys(this.pendingChanges).length > 0
    }
  },
  components: {
    MultiSelect,
    ReferenceManager,
    RoleManager,
    GroupManager,
    AuditTrailViewer
  },
  watch: {
    'managedUsers': function (users) {
      // Initialize selectedModels and originalModels with clients for every user
      this.selectedModels = {}
      this.originalModels = {}
      this.pendingChanges = {} // Reset pending changes when users are reloaded
      if (users) {
        users.forEach(element => {
          if (element.clients) {
            this.$set(this.selectedModels, element.id, [...element.clients])
            this.$set(this.originalModels, element.id, [...element.clients])
          } else {
            this.$set(this.selectedModels, element.id, [])
            this.$set(this.originalModels, element.id, [])
          }
        })
      }
      // Ensure loading is stopped once users are loaded
      if (this.currentTab === 0) {
        this.loading = false
      }
    },
    'currentTab': function (newTab) {
      // Reset loading state when switching to Users tab
      if (newTab === 0) {
        // If we have users data, stop loading immediately
        if (this.managedUsers && this.managedUsers.length > 0) {
          this.loading = false
        }
      }
    }
  },
  created: async function () {
    try {
      // Load data with progressive enhancement
      // loadManagedUsers resolves immediately with basic data, then loads permissions in background
      await Promise.all([this.loadManagedUsers(), this.loadAllModels()])
    } catch (error) {
      console.error('Error loading data:', error)
      this.showAlert({
        type: 'error',
        message: this.$t('views.manageUserModels.loadErrorMessage')
      })
    } finally {
      // Always stop loading spinner, even if there's an error
      this.loading = false
    }
  }
}
</script>

<style scoped>
.pending-changes {
  background-color: #fff3e0;
  border-left: 3px solid #ff9800;
  padding: 8px;
  border-radius: 4px;
  margin: 4px 0;
}
</style>
